<script setup lang="ts">
import { computed, onMounted, ref } from "vue";

import AppButton from "@shared/ui/components/AppButton.vue";
import AppInput from "@shared/ui/components/AppInput.vue";

import { useUser } from "@entities/user/useUser";

import { useAuth, type AuthMethod } from "@features/auth/useAuth";
import { touchIdSupported } from "@features/auth/useTouchId";

// Управление методами входа: смена PIN, добавление/удаление PIN, биометрии
// (WebAuthn PRF) и Touch ID (Tauri macOS). Все операции требуют
// разблокированной сессии (ключи в памяти).
const {
    getAuthMethods,
    addPasswordFallback,
    addPrfFallback,
    addTouchIdFallback,
    changePin,
    removeAuthMethod,
} = useAuth();
const { user, load: loadUser } = useUser();

const methods = ref<AuthMethod[]>([]);
const hasPin = computed(() => methods.value.includes("pin"));
const hasPrf = computed(() => methods.value.includes("prf"));
const hasTouchId = computed(() => methods.value.includes("touchid"));
const canRemove = computed(() => methods.value.length > 1);
// На Tauri-macOS Touch ID ЗАМЕНЯЕТ PRF (WebAuthn в WKWebView не работает).
const touchIdAvailable = touchIdSupported();
const prfAvailable = "credentials" in navigator && !touchIdAvailable;

// Какая форма раскрыта: смена PIN / добавление PIN / null.
const form = ref<"change" | "add" | null>(null);
const oldPin = ref("");
const newPin = ref("");
const newPin2 = ref("");
const busy = ref(false);
const error = ref<string | null>(null);
const success = ref<string | null>(null);

async function refresh(): Promise<void> {
    methods.value = await getAuthMethods();
}

onMounted(refresh);

function openForm(kind: "change" | "add"): void {
    form.value = kind;
    oldPin.value = "";
    newPin.value = "";
    newPin2.value = "";
    error.value = null;
    success.value = null;
}

function fail(e: unknown): void {
    if (e instanceof Error && e.message.startsWith("LOCKED:")) {
        error.value = `Слишком много попыток — подождите ${e.message.slice(7)} с`;
    } else if (e instanceof Error && e.message === "WRONG_PIN") {
        error.value = "Неверный текущий PIN";
    } else {
        error.value = "Не получилось — попробуйте ещё раз";
    }
}

async function submitPin(): Promise<void> {
    error.value = null;
    if (newPin.value.length < 4) {
        error.value = "PIN — минимум 4 символа";
        return;
    }
    if (newPin.value !== newPin2.value) {
        error.value = "PIN не совпадает";
        return;
    }
    busy.value = true;
    try {
        if (form.value === "change") {
            await changePin(oldPin.value, newPin.value);
            success.value = "PIN изменён";
        } else {
            await addPasswordFallback(newPin.value);
            success.value = "PIN добавлен";
        }
        form.value = null;
        await refresh();
    } catch (e) {
        fail(e);
    } finally {
        busy.value = false;
    }
}

async function addBiometrics(): Promise<void> {
    error.value = null;
    success.value = null;
    busy.value = true;
    try {
        if (!user.value) {
            await loadUser();
        }
        await addPrfFallback(user.value?.name ?? "Monster Chat");
        success.value = "Биометрия добавлена";
        await refresh();
    } catch {
        error.value = "Биометрия недоступна на этом устройстве";
    } finally {
        busy.value = false;
    }
}

async function addTouchId(): Promise<void> {
    error.value = null;
    success.value = null;
    busy.value = true;
    try {
        await addTouchIdFallback();
        success.value = "Touch ID добавлен";
        await refresh();
    } catch {
        error.value = "Touch ID недоступен на этом устройстве";
    } finally {
        busy.value = false;
    }
}

const METHOD_REMOVED: Record<AuthMethod, string> = {
    pin: "PIN удалён",
    prf: "Биометрия удалена",
    touchid: "Touch ID удалён",
};

async function removeMethod(method: AuthMethod): Promise<void> {
    error.value = null;
    success.value = null;
    busy.value = true;
    try {
        await removeAuthMethod(method);
        success.value = METHOD_REMOVED[method];
        form.value = null;
        await refresh();
    } catch (e) {
        if (e instanceof Error && e.message === "LAST_METHOD") {
            error.value = "Нельзя удалить единственный способ входа";
        } else {
            fail(e);
        }
    } finally {
        busy.value = false;
    }
}
</script>

<template>
    <section class="mc-sec">
        <h2 class="mc-sec__title">Безопасность</h2>

        <!-- PIN -->
        <div class="mc-sec__row">
            <div class="mc-sec__info">
                <span class="mc-sec__name">PIN-код</span>
                <span class="mc-sec__state">
                    {{ hasPin ? "Включён" : "Выключен" }}
                </span>
            </div>
            <div class="mc-sec__actions">
                <AppButton
                    v-if="hasPin"
                    size="sm"
                    variant="ghost"
                    :disabled="busy"
                    @click="openForm('change')"
                >
                    Сменить
                </AppButton>
                <AppButton
                    v-else
                    size="sm"
                    variant="ghost"
                    :disabled="busy"
                    @click="openForm('add')"
                >
                    Добавить
                </AppButton>
                <AppButton
                    v-if="hasPin && canRemove"
                    size="sm"
                    variant="danger"
                    :disabled="busy"
                    @click="removeMethod('pin')"
                >
                    Удалить
                </AppButton>
            </div>
        </div>

        <!-- Форма PIN -->
        <form v-if="form" class="mc-sec__form" @submit.prevent="submitPin">
            <AppInput
                v-if="form === 'change'"
                v-model="oldPin"
                type="password"
                placeholder="Текущий PIN"
            />
            <AppInput
                v-model="newPin"
                type="password"
                placeholder="Новый PIN"
            />
            <AppInput
                v-model="newPin2"
                type="password"
                placeholder="Повторите новый PIN"
            />
            <div class="mc-sec__actions">
                <AppButton size="sm" :disabled="busy" @click="submitPin">
                    {{ form === "change" ? "Сменить PIN" : "Добавить PIN" }}
                </AppButton>
                <AppButton
                    size="sm"
                    variant="ghost"
                    :disabled="busy"
                    @click="form = null"
                >
                    Отмена
                </AppButton>
            </div>
        </form>

        <!-- Биометрия (WebAuthn PRF) -->
        <div v-if="prfAvailable" class="mc-sec__row">
            <div class="mc-sec__info">
                <span class="mc-sec__name">Биометрия</span>
                <span class="mc-sec__state">
                    {{ hasPrf ? "Включена" : "Выключена" }}
                </span>
            </div>
            <div class="mc-sec__actions">
                <AppButton
                    v-if="!hasPrf"
                    size="sm"
                    variant="ghost"
                    :disabled="busy"
                    @click="addBiometrics"
                >
                    Добавить
                </AppButton>
                <AppButton
                    v-else-if="canRemove"
                    size="sm"
                    variant="danger"
                    :disabled="busy"
                    @click="removeMethod('prf')"
                >
                    Удалить
                </AppButton>
            </div>
        </div>

        <!-- Touch ID (Tauri macOS, Keychain за биометрией) -->
        <div v-if="touchIdAvailable" class="mc-sec__row">
            <div class="mc-sec__info">
                <span class="mc-sec__name">Touch ID</span>
                <span class="mc-sec__state">
                    {{ hasTouchId ? "Включён" : "Выключен" }}
                </span>
            </div>
            <div class="mc-sec__actions">
                <AppButton
                    v-if="!hasTouchId"
                    size="sm"
                    variant="ghost"
                    :disabled="busy"
                    @click="addTouchId"
                >
                    Добавить
                </AppButton>
                <AppButton
                    v-else-if="canRemove"
                    size="sm"
                    variant="danger"
                    :disabled="busy"
                    @click="removeMethod('touchid')"
                >
                    Удалить
                </AppButton>
            </div>
        </div>

        <p v-if="error" class="mc-sec__msg mc-sec__msg_error">{{ error }}</p>
        <p v-if="success" class="mc-sec__msg">{{ success }}</p>
    </section>
</template>

<style scoped lang="scss">
.mc-sec {
    display: flex;
    flex-direction: column;
    gap: 12px;

    &__title {
        margin: 0;
        font-size: 15px;
    }

    &__row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 10px 12px;
        background: var(--mc-bg-card);
        border: 1px solid var(--mc-line);
    }

    &__info {
        display: flex;
        flex-direction: column;
        gap: 2px;
    }

    &__name {
        font-size: 14px;
        color: var(--mc-fg);
    }

    &__state {
        font-family: var(--mc-mono);
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: var(--mc-fg-mute);
    }

    &__actions {
        display: flex;
        gap: 8px;
    }

    &__form {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    &__msg {
        margin: 0;
        font-size: 13px;
        color: var(--mc-acid);

        &_error {
            color: var(--mc-danger);
        }
    }
}
</style>
